/**
 * ToggleButton — a single segmented/mode toggle.
 *
 * Replaces the hand-rolled `modeBtn(active)` style helper that was
 * copy-pasted across pages for "pick one of these views/modes" rows.
 * Inactive renders as a neutral outline; active fills with the accent
 * glow and accent text/border. Group layout (the flex row + gap) stays
 * with the caller — this is just the one toggle's look.
 *
 * CSS-var primitive (not Tailwind): used by both the CSS-var pages
 * (e.g. RbacSimulator) and the inline-styled briefing panels.
 */

import { forwardRef, type ButtonHTMLAttributes } from 'react';

export interface ToggleButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Whether this toggle is the currently-selected one in its group. */
  active: boolean;
}

export const ToggleButton = forwardRef<HTMLButtonElement, ToggleButtonProps>(
  ({ active, style, type = 'button', disabled, ...rest }, ref) => (
    <button
      ref={ref}
      type={type}
      disabled={disabled}
      style={{
        padding: '6px 12px',
        fontSize: 13,
        fontWeight: 500,
        borderRadius: 6,
        border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
        background: active ? 'var(--accent-glow)' : 'transparent',
        color: active ? 'var(--accent)' : 'var(--text-primary)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        ...style,
      }}
      {...rest}
    />
  ),
);

ToggleButton.displayName = 'ToggleButton';
